import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, TicketTemplateRow } from '../api';
import { eventEndpoints, templateEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel } from '../ui';
import { INPUT } from './event/shared';

/**
 * Ticket templates (Phase 8, D-113): the printed ticket's sponsor logo and
 * header image, the wallet pass's own logo and hero, accent color,
 * sponsor line and terms per event, with one default. The PDF, the Google
 * Wallet pass and the BOCA ticket all read the same template.
 */
export default function TicketTemplatesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const templates = useQuery({ queryKey: ['ticket-templates'], queryFn: templateEndpoints.list });
  const [draft, setDraft] = useState({ name: '', accent_hex: '#7b1e2b', sponsor_line: '', terms_text: '' });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
  const create = useMutation({
    mutationFn: () => templateEndpoints.create({ name: draft.name, accent_hex: draft.accent_hex || null, sponsor_line: draft.sponsor_line || null, terms_text: draft.terms_text || null }),
    onSuccess: () => {
      setDraft({ name: '', accent_hex: '#7b1e2b', sponsor_line: '', terms_text: '' });
      setError(null);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-ink-900">Ticket templates</h1>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {(templates.data?.templates ?? []).map((t) => (
        <TemplateCard key={t.id} template={t} onError={(m) => setError(m)} onChanged={refresh} />
      ))}
      {can('events.manage') ? (
        <Panel title="New template">
          <form
            className="grid gap-3 px-5 py-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Name" htmlFor="tt-name">
              <input id="tt-name" className={`${INPUT} w-full`} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Accent color" htmlFor="tt-accent">
              <input id="tt-accent" type="color" className={INPUT} value={draft.accent_hex} onChange={(e) => setDraft({ ...draft, accent_hex: e.target.value })} />
            </Field>
            <Field label="Sponsor line" htmlFor="tt-sponsor" hint="Printed under the logo, e.g. Presented by …">
              <input id="tt-sponsor" className={`${INPUT} w-full`} value={draft.sponsor_line} onChange={(e) => setDraft({ ...draft, sponsor_line: e.target.value })} />
            </Field>
            <Field label="Terms text" htmlFor="tt-terms" hint="Replaces the standard terms block when set">
              <textarea id="tt-terms" className={`${INPUT} w-full`} rows={3} value={draft.terms_text} onChange={(e) => setDraft({ ...draft, terms_text: e.target.value })} />
            </Field>
            <div>
              <Button type="submit" variant="primary" disabled={!draft.name || create.isPending}>
                Create
              </Button>
            </div>
          </form>
        </Panel>
      ) : null}
    </div>
  );
}

type ImageKind = 'sponsor-logo' | 'header' | 'wallet-logo' | 'wallet-hero';

/**
 * The four pictures a template holds, and what each is for. The wallet pair is
 * separate from the printed pair on purpose (D-113): a sponsor logo says
 * "presented by", while the logo on a wallet pass is the issuer's own mark.
 * Leave the wallet pair empty and the pass falls back to the printed pair.
 */
const IMAGE_SLOTS: { kind: ImageKind; label: string; hint: string; file: (t: TicketTemplateRow) => string | null }[] = [
  { kind: 'sponsor-logo', label: 'Sponsor logo', hint: 'Printed ticket and PDF, under "presented by".', file: (t) => t.sponsor_logo_file_id },
  { kind: 'header', label: 'Header image', hint: 'Across the top of the printed ticket and PDF.', file: (t) => t.header_image_file_id },
  { kind: 'wallet-logo', label: 'Wallet pass logo', hint: 'Small and square, top of the phone pass. Falls back to the sponsor logo.', file: (t) => t.wallet_logo_file_id },
  { kind: 'wallet-hero', label: 'Wallet pass hero', hint: 'Wide banner across the pass, about 1032 × 336. Falls back to the header image.', file: (t) => t.wallet_hero_file_id },
];

function TemplateCard({ template, onError, onChanged }: { template: TicketTemplateRow; onError: (message: string) => void; onChanged: () => void }) {
  const can = usePermissions();
  const canManage = can('events.manage');
  const [eventId, setEventId] = useState('');
  const events = useQuery({ queryKey: ['events-list'], queryFn: () => eventEndpoints.list() });
  const assigned = useQuery({ queryKey: ['template-events', template.id], queryFn: () => templateEndpoints.events(template.id) });
  const queryClient = useQueryClient();
  const refreshAssigned = () => void queryClient.invalidateQueries({ queryKey: ['template-events', template.id] });
  const update = useMutation({ mutationFn: (input: Record<string, unknown>) => templateEndpoints.update(template.id, input), onSuccess: onChanged, onError: (c: ApiError) => onError(c.message) });
  const upload = useMutation({ mutationFn: ({ kind, file }: { kind: ImageKind; file: File }) => templateEndpoints.uploadImage(template.id, kind, file), onSuccess: onChanged, onError: (c: ApiError) => onError(c.message) });
  const remove = useMutation({ mutationFn: (kind: ImageKind) => templateEndpoints.removeImage(template.id, kind), onSuccess: onChanged, onError: (c: ApiError) => onError(c.message) });
  const assign = useMutation({ mutationFn: () => templateEndpoints.assign(template.id, eventId), onSuccess: () => { setEventId(''); refreshAssigned(); }, onError: (c: ApiError) => onError(c.message) });
  const unassign = useMutation({ mutationFn: (id: string) => templateEndpoints.unassign(template.id, id), onSuccess: refreshAssigned, onError: (c: ApiError) => onError(c.message) });
  const pick = (kind: ImageKind) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) upload.mutate({ kind, file });
    };
    input.click();
  };
  return (
    <Panel
      title={template.name}
      description={`${template.is_default ? 'Default for every event without its own template. ' : ''}${template.show_price ? 'Prices shown.' : 'Prices hidden.'}`}
      actions={
        <div className="flex items-center gap-2">
          {template.is_default ? <Badge tone="accent">default</Badge> : null}
          <Badge tone={template.active ? 'ok' : 'neutral'}>{template.active ? 'active' : 'inactive'}</Badge>
        </div>
      }
    >
      <div className="grid gap-4 px-5 py-4 md:grid-cols-3">
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-2">
            <span className="inline-block h-5 w-5 rounded" style={{ background: template.accent_hex ?? '#2563eb' }} /> Accent {template.accent_hex ?? 'default blue'}
          </p>
          <p>{template.sponsor_line ?? <span className="text-ink-400">No sponsor line</span>}</p>
          <p className="whitespace-pre-line text-xs text-ink-600">{template.terms_text ?? 'Standard terms'}</p>
          {canManage ? (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => { const v = window.prompt('Sponsor line', template.sponsor_line ?? ''); if (v !== null) update.mutate({ sponsor_line: v || null }); }}>Sponsor line</Button>
              <Button onClick={() => { const v = window.prompt('Terms text', template.terms_text ?? ''); if (v !== null) update.mutate({ terms_text: v || null }); }}>Terms</Button>
              <Button onClick={() => { const v = window.prompt('Accent color (#rrggbb)', template.accent_hex ?? '#7b1e2b'); if (v !== null) update.mutate({ accent_hex: v || null }); }}>Accent</Button>
              <Button onClick={() => update.mutate({ show_price: !template.show_price })}>{template.show_price ? 'Hide prices' : 'Show prices'}</Button>
              {!template.is_default ? <Button onClick={() => update.mutate({ is_default: true })}>Make default</Button> : null}
              <Button onClick={() => update.mutate({ active: !template.active })}>{template.active ? 'Deactivate' : 'Activate'}</Button>
            </div>
          ) : null}
        </div>
        <div className="space-y-3 text-sm">
          {IMAGE_SLOTS.map(({ kind, label, hint, file }) => {
            const fileId = file(template);
            return (
              <div key={kind}>
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
                <p className="text-xs text-ink-400">{hint}</p>
                {fileId ? <img src={`${templateEndpoints.imageUrl(template.id, kind)}?v=${template.updated_at}`} alt="" className="mt-1 max-h-20 rounded border border-line" /> : <p className="text-ink-400">None</p>}
                {canManage ? (
                  <div className="mt-1 flex gap-2">
                    <Button onClick={() => pick(kind)} disabled={upload.isPending}>Upload PNG/JPEG</Button>
                    {fileId ? <Button variant="danger" onClick={() => remove.mutate(kind)}>Remove</Button> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="text-sm">
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Events using this template</p>
          <ul className="mt-1 space-y-1">
            {(assigned.data?.events ?? []).map((e) => (
              <li key={e.id} className="flex items-center justify-between">
                <span>{e.title}</span>
                {canManage ? <button type="button" onClick={() => unassign.mutate(e.id)} className="text-xs text-ink-400 underline">remove</button> : null}
              </li>
            ))}
            {(assigned.data?.events ?? []).length === 0 ? <li className="text-ink-400">{template.is_default ? 'Every event without its own template.' : 'None.'}</li> : null}
          </ul>
          {canManage ? (
            <div className="mt-2 flex gap-2">
              <select className={INPUT} value={eventId} onChange={(e) => setEventId(e.target.value)} aria-label="Event">
                <option value="">Assign to event…</option>
                {(events.data?.events ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
              </select>
              <Button disabled={!eventId || assign.isPending} onClick={() => assign.mutate()}>Assign</Button>
            </div>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
